import { useCallback, useEffect, useState, type RefObject } from 'react';

export type PlaybackStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'ended' | 'error';

export type UsePlaybackResult = {
  status: PlaybackStatus;
  currentMs: number;
  durationMs: number;
  load: (source: Blob | string) => void;
  play: () => Promise<void>;
  pause: () => void;
  seekTo: (ms: number) => void;
};

export function usePlayback(ref: RefObject<HTMLAudioElement | null>): UsePlaybackResult {
  const [status, setStatus] = useState<PlaybackStatus>('idle');
  const [currentMs, setCurrentMs] = useState<number>(0);
  const [durationMs, setDurationMs] = useState<number>(0);

  useEffect(() => {
    const audio = ref.current;
    if (!audio) return;

    const handleLoadedMetadata = (): void => {
      const safeDuration = Number.isFinite(audio.duration) ? audio.duration : 0;
      setDurationMs(Math.round(safeDuration * 1000));
      setStatus('paused');
    };
    const handlePlay = (): void => setStatus('playing');
    const handlePause = (): void => setStatus('paused');
    const handleEnded = (): void => setStatus('ended');
    const handleError = (): void => setStatus('error');
    const handleTimeUpdate = (): void => {
      setCurrentMs(Math.round(audio.currentTime * 1000));
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [ref]);

  const load = useCallback(
    (source: Blob | string): void => {
      const audio = ref.current;
      if (!audio) return;
      audio.src = typeof source === 'string' ? source : URL.createObjectURL(source);
      setStatus('loading');
      audio.load();
    },
    [ref],
  );

  const play = useCallback(async (): Promise<void> => {
    const audio = ref.current;
    if (!audio) return;
    await audio.play();
  }, [ref]);

  const pause = useCallback((): void => {
    ref.current?.pause();
  }, [ref]);

  const seekTo = useCallback(
    (ms: number): void => {
      const audio = ref.current;
      if (!audio) return;
      audio.currentTime = ms / 1000;
    },
    [ref],
  );

  return { status, currentMs, durationMs, load, play, pause, seekTo };
}
